import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  IGeoTestConverterApiOperation,
  IGeoTestConverterMetadata
} from '../models/IGeoTestConverterApiOperation';
import { ICampaignSnapShot } from '../models/geo-test/ICreateGeoTestRequest';
import { ICPAdStudyConfig } from '../models/geo-test/ICPAdStudyConfig';
import { ITacticsByPlatformRequest } from '../models/geo-test/ITacticsByPlatformRequest';
import { IListCampaignsRequest } from '../models/geo-test/IListCampaignsRequest';
import { ICampaign } from '../models/ICampaign';
import { IGeoTest } from '../models/geo-test/IGeoTest';
import { Utility } from '../helpers/utility';
import { VendorConfiguredAt, VendorLevel } from '@libs/apis';
import { IStudyConversionActivity } from '../models/IStudyConversionActivity';

const promiseFrom = lastValueFrom;

export class GeoTestConverterApis implements IGeoTestConverterApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string,
    private readonly apiV2: string
  ) {}

  public static geoTestConverterApiOperation(
    httpClient: HttpClient,
    apiV1: string,
    apiV2: string
  ): IGeoTestConverterApiOperation {
    return new GeoTestConverterApis(httpClient, apiV1, apiV2);
  }

  public async getContributionOnlyGeoTests(
    clientId: number,
    brandId: number,
    statuses: string
  ): Promise<ICPAdStudyConfig[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/geo-tests/contribution-only`,
      queryParameters: { clientId, brandId, statuses }
    });

    return promiseFrom(this.httpClient.get<ICPAdStudyConfig[]>(url));
  }

  public async getPlatforms(
    clientId: number,
    brandId: number,
    conversionTypeId?: number
  ): Promise<any[]> {
    const queryParams: any = { clientId, brandId, conversionTypeId };

    const url = Utility.buildURL({
      url: `${this.apiV2}/cross-platform-doe/data-sources`,
      queryParameters: queryParams
    });

    return promiseFrom(this.httpClient.get<any[]>(url));
  }

  public async getTacticsByPlatform(
    request: ITacticsByPlatformRequest
  ): Promise<{ tactic: string }[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/geo-tests/tactics-without-recommendations`
    });

    return promiseFrom(
      this.httpClient.post<{ tactic: string }[]>(url, request)
    );
  }

  public async getGeoTestCampaigns(
    listCampaignsRequest: Partial<IListCampaignsRequest>
  ): Promise<ICampaign[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/geo-tests/campaigns-without-recommendation`,
      queryParameters: { clientId: listCampaignsRequest.clientId }
    });

    return promiseFrom(
      this.httpClient.post<ICampaign[]>(url, listCampaignsRequest)
    );
  }

  public async convertToIncTest(
    studyId: number,
    campaigns: ICampaign[],
    metadata: IGeoTestConverterMetadata,
    campaignSnapShot: ICampaignSnapShot[],
    vendorLevel: VendorLevel,
    vendorConfiguredAt: VendorConfiguredAt
  ): Promise<IGeoTest> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/geo-tests/convertToIncTest/:studyId`,
      pathVariables: { studyId }
    });

    const requestBody = {
      campaigns,
      metadata,
      campaignSnapShot,
      convertToIncrementality: true,
      vendorLevel,
      vendorConfiguredAt
    };

    return promiseFrom(this.httpClient.patch<IGeoTest>(url, requestBody));
  }

  public async getContributionToIncStudyConversionActivity(
    clientId: number,
    brandId: number
  ): Promise<IStudyConversionActivity[]> {
    const url = Utility.buildURL({
      url: `${this.apiV1}/geo-tests/study-conversion-activity`,
      queryParameters: { clientId, brandId }
    });

    return promiseFrom(this.httpClient.get<IStudyConversionActivity[]>(url));
  }

  public async logConversionError(
    clientId: number,
    brandId: number,
    studyId: number,
    studyName: string,
    errorMessage: string
  ): Promise<void> {
    const url = `${this.apiV1}/geo-tests/log-conversion-error`;

    const requestBody = {
      clientId,
      brandId,
      studyId,
      studyName,
      errorMessage
    };

    return promiseFrom(this.httpClient.post<void>(url, requestBody));
  }
}
